import { api } from '@/utils/api'

export interface ChatRequest {
  message: string
  metadata?: Record<string, any>
}

export interface ChatResponse {
  success: boolean
  conversation_id: string
  message_id: string
  assistant_message: string
}

export interface Conversation {
  conversation_id: string
  user_id: string
  title?: string
  created_at: number
  updated_at: number
  metadata?: Record<string, any>
  messages: ChatMessage[]
}

export interface ChatMessage {
  message_id: string
  role: 'system' | 'user' | 'assistant'
  content: string
  timestamp: number
  metadata?: Record<string, any>
}

export interface ConversationListItem {
  conversation_id: string
  title?: string
  created_at: number
  updated_at: number
  message_count: number
  last_message?: string
}

export interface ConversationsResponse {
  success: boolean
  conversations: ConversationListItem[]
}

export interface StreamingChatChunk {
  conversation_id: string
  message_id: string
  chunk: string
  is_complete: boolean
  full_response?: string
  error?: boolean
  error_message?: string
}

// Autocomplete interfaces matching backend Pydantic models
export interface TargetUsernameAutocompleteResult {
  id: string
  target_username: string
  type: string
  category: string
  timestamp: string
}

export interface AutocompleteRequest {
  prefix: string
  limit?: number
  search_instagram_username?: boolean
}

export interface AutocompleteHealthResponse {
  status: string
  service: string
  message: string
}

export const chatService = {
  async sendStreamingMessage(
    conversationId: string,
    request: ChatRequest,
    onChunk: (chunk: StreamingChatChunk) => void,
    onError?: (error: Error) => void,
    onComplete?: (fullResponse: string) => void
  ): Promise<void> {
    const token = localStorage.getItem('authToken')
    const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'
    
    const url = `${API_BASE_URL}/v0_1/chat/${conversationId}/send`
    console.log('🌐 ChatService: About to fetch:', url)
    console.log('🌐 ChatService: Request payload:', request)
    console.log('🌐 ChatService: Auth token present:', !!token)
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(request),
      })

      console.log('📡 ChatService: Fetch response status:', response.status)
      console.log('📡 ChatService: Fetch response ok:', response.ok)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ ChatService: Response not ok:', errorText)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        console.error('❌ ChatService: Response body is not readable')
        throw new Error('Response body is not readable')
      }

      console.log('📖 ChatService: Starting to read stream...')
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          console.log('✅ ChatService: Stream reading completed')
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        console.log('📦 ChatService: Raw chunk received:', chunk)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.slice(6)
              console.log('🔍 ChatService: Parsing JSON:', jsonData)
              const data = JSON.parse(jsonData) as StreamingChatChunk
              console.log('✅ ChatService: Parsed chunk:', data)
              onChunk(data)

              if (data.error) {
                console.error('❌ ChatService: Chunk contains error:', data.error_message)
                onError?.(new Error(data.error_message || 'Unknown error'))
                return
              }

              if (data.is_complete && data.full_response) {
                console.log('🏁 ChatService: Stream complete with full response')
                onComplete?.(data.full_response)
                return
              }
            } catch (parseError) {
              console.warn('⚠️ ChatService: Failed to parse SSE data:', parseError, 'Raw line:', line)
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ ChatService: Fetch error:', error)
      onError?.(error instanceof Error ? error : new Error(String(error)))
    }
  },

  async getUserConversations(): Promise<ConversationsResponse> {
    return api.get('/v0_1/chat/')
  },

  async getConversation(conversationId: string): Promise<Conversation> {
    return api.get(`/v0_1/chat/${conversationId}`)
  },

  async updateConversationTitle(conversationId: string, title: string): Promise<Conversation> {
    const params = new URLSearchParams({ title })
    return api.put(`/v0_1/chat/${conversationId}/title?${params}`)
  },

  async createConversation(prompt: string): Promise<Conversation> {
    const params = new URLSearchParams({ prompt })
    return api.post(`/v0_1/chat/conversation?${params}`)
  },

  // Autocomplete methods
  async autocompleteTargetUsername(request: AutocompleteRequest): Promise<TargetUsernameAutocompleteResult[]> {
    const params = new URLSearchParams({
      prefix: request.prefix,
      ...(request.limit && { limit: request.limit.toString() }),
      ...(request.search_instagram_username !== undefined && { 
        search_instagram_username: request.search_instagram_username.toString() 
      }),
    })
    return api.get(`/v0_1/chat/autocomplete/target-username?${params}`)
  },

  async autocompleteHealthCheck(): Promise<AutocompleteHealthResponse> {
    return api.get('/v0_1/chat/autocomplete/health')
  },
} 